import classNames from "classnames"
import {
  Cabinet,
  Dashboard,
  Euro,
  FoodMenu,
  Home,
  InvoiceIcon,
  PaperPlane,
  Pickup,
  Settings,
  Stats,
  Store,
  Verified,
} from "components/LEGACY/styled-components/Icons"
import TokenAuthenticator from "components/TokenAuthenticator"
import Login from "components/pages/Login"
import Sidebar from "components/utils/navigation/Sidebar"
import SidebarLink from "components/utils/navigation/SidebarLink"
import { getAuth, onAuthStateChanged } from "firebase/auth"
import { useAccount } from "hooks/firestore/simple/useAccount"
import { useLanguage } from "hooks/firestore/simple/useLanguage"
import { useSettings } from "hooks/firestore/simple/useSettings"
import { useShop } from "hooks/firestore/simple/useShop"
import { useUser } from "hooks/firestore/simple/useUser"
import { useCurrentUser } from "hooks/localstate/auth/useCurrentUser"
import { useRole } from "hooks/misc/useRole"
import { getTranslation } from "i18n/util"
import { useRouter } from "next/router"
import { ReactNode, useEffect, useState } from "react"
import { AdminShopIdContextProvider } from "routers/AdminShopIdContextProvider"
import IntercomProvider from "routers/IntercomProvider"
import styles from "./AdminLayout.module.scss"

const SidebarLinks = () => {
  const settings = useSettings()
  const shop = useShop()
  const account = useAccount()
  const user = useUser()
  const role = useRole()
  const currentUser = useCurrentUser()
  const language = useLanguage()
  const verified = currentUser?.emailVerified && !!settings && (settings.verified ?? true)

  const isClickAndCollectApotheke = settings?.customShopPreset === "click-and-collect-apotheke"

  return (
    <>
      <style jsx>{`
        .shopDashboardButtonHeader {
          display: flex;
          align-items: center;
          white-space: normal;
          line-height: 1;
        }
      `}</style>
      {shop && verified && role && ["superadmin", "admin", "member", "guest"].includes(role) ? (
        <>
          <SidebarLink icon={Home} href="/admin/dashboard" markedTop>
            <div className="shopDashboardButtonHeader">
              {`${shop.name}`}
              {shop.platformListed && <Verified style={{ marginLeft: "0.5rem" }} color="currentColor" size="16" />}
            </div>
          </SidebarLink>

          {shop.offering?.offeringId && !isClickAndCollectApotheke && (
            <>
              <SidebarLink icon={Store} href={`/${shop.shorthand}?admin`} newTab>
                {getTranslation(language, "sidebar.internalOrdering")}
              </SidebarLink>

              {settings.b2b?.active && (
                <SidebarLink icon={Store} href={`/${shop.shorthand}?b2b=true`} newTab>
                  {getTranslation(language, "sidebar.b2bOrdering")}
                </SidebarLink>
              )}
              <SidebarLink icon={Store} href={`/${shop.shorthand}`} markedBottom newTab>
                {getTranslation(language, "sidebar.customerView")}
              </SidebarLink>
            </>
          )}

          {isClickAndCollectApotheke ? (
            <SidebarLink icon={Dashboard} href="/admin/penguin-locker-orders">
              {getTranslation(language, "sidebar.orders")}
            </SidebarLink>
          ) : (
            <SidebarLink icon={Dashboard} href="/admin/orders">
              {getTranslation(language, "sidebar.orders")}
            </SidebarLink>
          )}
          {account?.penguinLockersLogin?.apiKey && settings.featurePenguinLockers && (
            <SidebarLink icon={Cabinet} href="/admin/machines">
              {getTranslation(language, "sidebar.machines")}
            </SidebarLink>
          )}
          {account?.clickAndCollectAutomatenLogin?.apiKey && settings.featureClickAndCollectAutomaten && (
            <SidebarLink icon={Cabinet} href="/admin/c-and-c-machines">
              {getTranslation(language, "sidebar.cAndCMachines")}
            </SidebarLink>
          )}
          {!isClickAndCollectApotheke && (
            <SidebarLink icon={Stats} href="/admin/statistics">
              {getTranslation(language, "sidebar.statistics")}
            </SidebarLink>
          )}
        </>
      ) : (
        <SidebarLink icon={Home} href="/admin/start">
          {getTranslation(language, "sidebar.start")}
        </SidebarLink>
      )}
      {!isClickAndCollectApotheke && shop && verified && role && ["superadmin", "admin"].includes(role) ? (
        <>
          <SidebarLink
            icon={FoodMenu}
            href={shop && !shop.offering?.offeringId ? "/admin/createOffering" : "/admin/assortment"}
          >
            {getTranslation(language, "sidebar.assortmentEditor")}
          </SidebarLink>
          <SidebarLink
            icon={Store}
            href={shop && !shop.offering?.offeringId ? "/admin/createOffering" : "/admin/storefront"}
          >
            {getTranslation(language, "sidebar.shopEditor")}
          </SidebarLink>
        </>
      ) : null}
      <SidebarLink icon={PaperPlane} href="/admin/marketing">
        {getTranslation(language, "sidebar.marketing")}
      </SidebarLink>
      {shop?.featureLoginRequired && verified && role && ["superadmin", "admin", "member"].includes(role) ? (
        <SidebarLink icon={Pickup} href="/admin/clients">
          {getTranslation(language, "sidebar.clients")}
        </SidebarLink>
      ) : null}
      {settings?.b2b?.active && verified && role && ["superadmin", "admin"].includes(role) ? (
        <SidebarLink icon={Euro} href="/admin/b2b">
          {getTranslation(language, "sidebar.b2b")}
        </SidebarLink>
      ) : null}
      {shop && verified && role && ["superadmin", "admin"].includes(role) ? (
        <SidebarLink icon={Settings} href="/admin/settings">
          {getTranslation(language, "sidebar.settings")}
        </SidebarLink>
      ) : null}
      {user?.getpackedAdmin ? (
        <SidebarLink icon={Settings} href="/admin/gp">
          {getTranslation(language, "sidebar.getpackedInternal")}
        </SidebarLink>
      ) : null}

      {!isClickAndCollectApotheke && (
        <SidebarLink icon={InvoiceIcon} href="/admin/my-b2b">
          {getTranslation(language, "sidebar.businessCustomerArea")}
        </SidebarLink>
      )}

      <SidebarLink icon={Store} href="/admin/my-shops" marked bottom>
        {getTranslation(language, "sidebar.myShops")}
      </SidebarLink>
    </>
  )
}

interface AdminLayoutProps {
  children: ReactNode
}
const AdminLayout = ({ children }: AdminLayoutProps) => {
  const [sidebarState, setSidebarState] = useState<boolean | undefined>()
  const router = useRouter()
  const [signedIn, setSignedIn] = useState(true)
  const toggleSidebar = () => {
    setSidebarState(state => (state !== undefined ? !state : window.innerWidth < 768 ? true : false))
  }

  useEffect(() => {
    if (router.pathname && window.innerWidth < 480) {
      setSidebarState(sidebarState => sidebarState && undefined)
    }
  }, [router.pathname])

  useEffect(() => {
    return onAuthStateChanged(getAuth(), user => {
      setSignedIn(!!user)
    })
  }, [])

  return (
    <IntercomProvider>
      <AdminShopIdContextProvider>
        <TokenAuthenticator />
        {signedIn ? (
          <div
            className={classNames({
              [styles.main]: true,
              [styles.sidebarOpen]: sidebarState === true,
              [styles.sidebarClosed]: sidebarState === false,
            })}
          >
            <Sidebar>
              <SidebarLinks />
            </Sidebar>
            <button className={styles.sidebarToggle} onClick={toggleSidebar}></button>
            <div className={styles.content}>{children}</div>
          </div>
        ) : (
          <Login />
        )}
      </AdminShopIdContextProvider>
    </IntercomProvider>
  )
}

export default AdminLayout
